/**
 * Activity Timeline Component
 * Displays inventory activity history in a table format with detailed modal
 */

import React, { useState } from 'react';
import { format } from 'date-fns';

const ActivityTimeline = ({ 
  activities = [], 
  loading = false, 
  onLoadMore, 
  hasMore = false,
  showFilters = true 
}) => {
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedActivityLog, setSelectedActivityLog] = useState(null);

  // Format event type for display
  const formatEventType = (eventType) => {
    return eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get event type color
  const getEventTypeColor = (eventType) => {
    if (eventType === 'BULK_CREATED') return 'bg-purple-100 text-purple-800';
    if (eventType === 'BULK_DELETED') return 'bg-red-100 text-red-800';
    if (eventType.includes('CREATED')) return 'bg-green-100 text-green-800';
    if (eventType.includes('UPDATED')) return 'bg-blue-100 text-blue-800';
    if (eventType.includes('DELETED')) return 'bg-red-100 text-red-800';
    if (eventType.includes('STATUS')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    if (filter !== 'all' && activity.eventType !== filter) {
      return false;
    }
    
    if (dateRange !== 'all') {
      const activityDate = new Date(activity.eventDate);
      const now = new Date();
      
      switch (dateRange) {
        case 'today':
          return activityDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return activityDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return activityDate >= monthAgo;
        default:
          return true;
      }
    }
    
    return true;
  });

  // Get unique event types for filter
  const eventTypes = [...new Set(activities.map(a => a.eventType).filter(type => type))];

  // Get item information
  const getItemInfo = (log) => {
    // Handle bulk operations
    if (log.eventType === 'BULK_CREATED' || log.eventType === 'BULK_DELETED') {
      try {
        const details = JSON.parse(log.details || '{}');
        return `${details.itemCount || 'Unknown'} items (${details.productTypeName || 'Unknown type'})`;
      } catch {
        return 'Bulk operation';
      }
    }
    
    // Handle product type operations
    if (log.eventType?.includes('PRODUCT_TYPE')) {
      try {
        const details = JSON.parse(log.details || '{}');
        return details.productTypeName || 'Unknown product type';
      } catch {
        return 'Product type';
      }
    }
    
    // Handle regular item operations
    if (log.item && log.item.itemNumber) {
      return `${log.item.itemNumber} (${log.item.productType?.name || 'Unknown type'})`;
    }
    
    // Try to get from details
    try {
      const details = JSON.parse(log.details || '{}');
      if (details.itemNumber) {
        return `${details.itemNumber} (${details.productTypeName || details.productType || 'Unknown type'})`;
      }
    } catch {
      // Ignore parse errors
    }
    
    return '—';
  };

  // Get fields changed
  const getFieldsChanged = (log) => {
    if (log.oldStatus && log.newStatus) {
      return `${log.oldStatus} → ${log.newStatus}`;
    }
    
    try {
      const details = JSON.parse(log.details || '{}');
      if (details.changes) {
        const changes = Object.keys(details.changes);
        return changes.length > 0 ? changes.join(', ') : '—';
      }
      if (details.oldValues && details.newValues) {
        const changedFields = Object.keys(details.oldValues);
        return changedFields.length > 0 ? changedFields.join(', ') : '—';
      }
    } catch {
      // Ignore parse errors
    }
    
    return '—';
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading activity logs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="all">All Events</option>
                {eventTypes.map(eventType => (
                  <option key={eventType} value={eventType}>
                    {formatEventType(eventType)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Past Week</option>
                <option value="month">Past Month</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
            <p className="text-gray-600">
              {filter !== 'all' || dateRange !== 'all' 
                ? 'No activities match the selected filters.'
                : 'Inventory activity will appear here as changes are made.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Item #
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Item Type
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Type of Update
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Date/Time
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Fields Changed
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    User
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActivities.map((log, index) => (
                  <tr key={log.id || index} className="hover:bg-gray-50">
                    <td className="px-2 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                      {log.item?.itemNumber || (() => {
                        try {
                          const details = JSON.parse(log.details || '{}');
                          return details.itemNumber || '—';
                        } catch { return '—'; }
                      })()}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-600">
                      {log.item?.productType?.name || (() => {
                        try {
                          const details = JSON.parse(log.details || '{}');
                          return details.productTypeName || details.productType || '—';
                        } catch { return '—'; }
                      })()}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(log.eventType)}`}>
                        {formatEventType(log.eventType)}
                      </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-600">
                      {format(new Date(log.eventDate), 'MMM d, h:mm a')}
                    </td>
                    <td className="px-2 py-3 text-xs text-gray-600 truncate">
                      {getFieldsChanged(log)}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-600">
                      <div className="flex items-center">
                        <svg className="h-3 w-3 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'}</span>
                      </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-center">
                      <button 
                        onClick={() => setSelectedActivityLog(log)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 inline-flex items-center justify-center"
                        title="View details"
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Load More */}
        {hasMore && !loading && (
          <div className="border-t border-gray-200 p-4 text-center">
            <button
              onClick={onLoadMore}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Load More Activities
            </button>
          </div>
        )}
        
        {/* Loading More */}
        {loading && activities.length > 0 && (
          <div className="border-t border-gray-200 p-4 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* Activity Log Details Modal */}
      {selectedActivityLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Activity Log Details
                </h3>
                <button
                  onClick={() => setSelectedActivityLog(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Activity Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Event Type</label>
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getEventTypeColor(selectedActivityLog.eventType)}`}>
                      {formatEventType(selectedActivityLog.eventType)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date & Time</label>
                    <p className="text-sm text-gray-900 mt-1">{format(new Date(selectedActivityLog.eventDate), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">User</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedActivityLog.user ? `${selectedActivityLog.user.firstName} ${selectedActivityLog.user.lastName}` : 'System'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Item</label>
                    <p className="text-sm text-gray-900 mt-1">{getItemInfo(selectedActivityLog)}</p>
                  </div>
                </div>
              </div>

              {/* Before/After Comparison */}
              {selectedActivityLog.details && (() => {
                try {
                  const details = JSON.parse(selectedActivityLog.details);
                  const hasChanges = details.changes || details.oldValues;
                  
                  if (hasChanges) {
                    return (
                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3">Changes Made</h4>
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2 bg-red-50 px-3 py-1 rounded">Before</h5>
                            <div className="space-y-2">
                              {details.oldValues ? Object.entries(details.oldValues).map(([key, value]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-700">{key}:</span>
                                  <span className="ml-2 text-gray-600">{value !== null ? String(value) : 'null'}</span>
                                </div>
                              )) : details.changes && Object.entries(details.changes).map(([key, change]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-700">{key}:</span>
                                  <span className="ml-2 text-gray-600">{change.from !== null ? String(change.from) : 'null'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2 bg-green-50 px-3 py-1 rounded">After</h5>
                            <div className="space-y-2">
                              {details.newValues ? Object.entries(details.newValues).map(([key, value]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-700">{key}:</span>
                                  <span className="ml-2 text-gray-600">{value !== null ? String(value) : 'null'}</span>
                                </div>
                              )) : details.changes && Object.entries(details.changes).map(([key, change]) => (
                                <div key={key} className="text-sm">
                                  <span className="font-medium text-gray-700">{key}:</span>
                                  <span className="ml-2 text-gray-600">{change.to !== null ? String(change.to) : 'null'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                } catch {
                  // Ignore parse errors
                }
                return null;
              })()}

              {/* Raw Details */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Raw Details</h4>
                <div className="bg-gray-100 rounded-lg p-3 text-sm font-mono text-gray-700 overflow-auto max-h-40">
                  {selectedActivityLog.details ? (
                    <pre>{JSON.stringify(JSON.parse(selectedActivityLog.details), null, 2)}</pre>
                  ) : (
                    'No additional details available'
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedActivityLog(null)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;